import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  Menu,
  LogOut,
  ClipboardList,
  FileStack,
  Moon,
  Sun,
  DollarSign,
  MapPinned,
  UserRoundCog
} from 'lucide-react';
import '../styles/Admin.css';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [darkMode, setDarkMode] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  React.useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("adminSelectedRole");
    navigate("/");
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const [adminUser, setAdminUser] = React.useState(() => {
    try {
      const saved = localStorage.getItem("adminUser");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const role = adminUser?.Role || adminUser?.role;
  const isEmployee = role && String(role).toLowerCase() === 'employee';
  const isAdmin = role && String(role).toLowerCase() === 'admin';

  // For debugging - will show in console
  console.log("Admin Data:", adminUser);
  console.log("Role:", role);
  console.log("Is Employee:", isEmployee);

  const menuItems = [
    {
      name: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
      isActive: location.pathname === '/admin/dashboard'
    },
    {
      name: 'Applications',
      icon: FileText,
      path: '/admin/applications',
      isActive: location.pathname === '/admin/applications' || location.pathname.startsWith('/admin/application/')
    },
    {
      name: 'Users',
      icon: Users,
      path: '/admin/users',
      isActive: location.pathname === '/admin/users' || location.pathname.startsWith('/admin/user/')
    },
    {
      name: 'Employees',
      icon: UserRoundCog,
      path: '/admin/employees',
      isActive: location.pathname === '/admin/employees'
    },
    {
      name: 'Payments',
      icon: DollarSign,
      path: '/admin/payments',
      isActive: location.pathname === '/admin/payments'
    },
    {
      name: 'Payment Settings',
      icon: Settings,
      path: '/admin/payment-settings',
      isActive: location.pathname === '/admin/payment-settings'
    },
    {
      name: 'Services',
      icon: ClipboardList,
      path: '/admin/services',
      isActive: location.pathname === '/admin/services'
    },
    {
      name: 'Form Fields',
      icon: FileStack,
      path: '/admin/form-fields',
      isActive: location.pathname === '/admin/form-fields'
    },
    {
      name: 'Service State Access',
      icon: MapPinned,
      path: '/admin/service-state-access',
      isActive: location.pathname === '/admin/service-state-access'
    }
  ];

  // Logic: 
  // 1. If Employee: only show Dashboard, Applications, Users.
  // 2. If Admin: show everything.
  // 3. If Role missing (case before login fix): restrict to employee-like view for safety? 
  // Actually, let's just filter based on role properly now that the login fix is in.
  const filteredMenuItems = isEmployee
    ? menuItems.filter(item => ['Dashboard', 'Applications', 'Users'].includes(item.name))
    : menuItems;

  // Check if current page is allowed
  const isAllowedPath = () => {
    if (!isEmployee) return true;

    const path = location.pathname;
    const allowedPatterns = [
      '/admin/dashboard',
      '/admin/applications',
      '/admin/users',
      '/admin/application/',
      '/admin/user/'
    ];

    return allowedPatterns.some(pattern => path.startsWith(pattern));
  };

  React.useEffect(() => {
    if (isEmployee && !isAllowedPath()) {
      navigate('/admin/dashboard');
    }
  }, [location.pathname, isEmployee]);

  if (isEmployee && !isAllowedPath()) {
    return null; // Don't render anything while redirecting
  }

  return (
    <div className="admin-layout">
      {/* Top Navbar */}
      <div className="admin-top-navbar">
        <div className="admin-top-navbar-left">
          <button
            className="admin-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu size={24} />
          </button>
          <div className="admin-brand">
            <h2>RTO Admin Panel</h2>
          </div>
        </div>
        <div className="admin-top-navbar-right">
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <div className="admin-user-info">
            <div className="admin-avatar">
              {adminUser?.username?.[0]?.toUpperCase() || adminUser?.Name?.[0]?.toUpperCase() || 'A'}
            </div>
            <div className="admin-user-details">
              <span className="admin-username">
                {adminUser?.username || adminUser?.Name || 'Admin'}
              </span>
              <span className="admin-role">{role || 'Administrator'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-body">
        {/* Sidebar */}
        <aside className={`admin-sidebar-new ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <nav className="admin-sidebar-nav">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  className={`admin-sidebar-item ${item.isActive ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                >
                  <Icon size={20} />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </nav>

          <div className="admin-sidebar-footer">
            <button
              className="admin-sidebar-item logout-btn"
              onClick={handleLogout}
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="admin-main-content">
          {children}
        </main>
      </div>

      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
